import { difference, each, isEqual, keys } from "lodash";
import moment from "moment";
import { OUTER, POSTCODE, SECTOR } from "../consts/layers";
import { Postcode } from "./postcode";
import { applyChangeToRouteConfig } from "./routeConfig";
import { applyChangeToRedirectConfig } from "./redirectConfig";
import { applyChangeToRoutes, applyChangeToTimesAndVolumes } from "./routes";

export const processRoutesInTimebandWithOverlay = ({
  routesInTimeBand,
  overlay,
  postcodeAllocation,
  routesObject,
  selectedTimeBand,
  overlayDetails,
}) => {
  routesInTimeBand = {
    outer: routesInTimeBand.routeOuter,
    sector: routesInTimeBand.routeSector,
    postcode: routesInTimeBand.routePostcode,
  };
  overlay = {
    outer: overlay.overlayOuter,
    sector: overlay.overlaySector,
    postcode: overlay.overlayPostcode,
  };
  const { outer, sector, postcode } = ["outer", "sector", "postcode"].reduce(
    (routesByType, type) => {
      Object.keys(routesInTimeBand[type]).forEach((key) => {
        if (!routesInTimeBand[type][key].routeCode) return;
        const time = moment(routesInTimeBand[type][key].startTime, "HH:mm")
          .subtract(15, "m")
          .format("HH:mm");
        const startTimeOverlay = getStartTimeOverlay({
          time,
          type,
          overlayDetails,
        });
        const currentOverlay = getStartTimeOverlay({
          time: selectedTimeBand,
          type,
          overlayDetails,
        });

        routesByType[type][key] = {
          ...routesInTimeBand[type][key],
          totalStops:
            time === "22:45"
              ? currentOverlay[key]?.stopsCumulativeTotal
              : currentOverlay[key]?.stopsCumulativeTotal -
                startTimeOverlay[key]?.stopsCumulativeTotal,
          [type]: key,
        };
      });

      Object.keys(overlay[type]).forEach((key) => {
        if (routesByType[type][key]) return;
        const time = getStartTimeFromAllocation({
          key,
          postcodeAllocation,
          routesObject,
          selectedTimeBand,
        });
        const startTimeOverlay = getStartTimeOverlay({
          time,
          type,
          overlayDetails,
        });

        let routeCode, plannedReadyTime, startTime;
        if (type === "sector")
          ({ routeCode, plannedReadyTime, startTime } =
            routesByType["outer"][Postcode.toOuter(key)] || {});
        else if (type === "postcode")
          ({ routeCode, plannedReadyTime, startTime } =
            routesByType["sector"][Postcode.toSector(key)] || {});

        routesByType[type][key] = {
          routeCode,
          plannedReadyTime,
          startTime,
          [type]: key,
          totalStops:
            time === "-23:00"
              ? overlay[type][key]?.stopsCumulativeTotal
              : overlay[type][key]?.stopsCumulativeTotal -
                startTimeOverlay[key]?.stopsCumulativeTotal,
        };
      });
      return routesByType;
    },
    { outer: {}, sector: {}, postcode: {} }
  );

  return {
    outer: Object.values(outer),
    sector: Object.values(sector),
    postcode: Object.values(postcode),
  };
};

const getStartTimeOverlay = ({ time, type, overlayDetails }) => {
  const startTime = overlayDetails?.find((detail) => detail.timeBand === time);

  let startTimeOverlay = {};
  if (startTime) {
    if (type === "postcode") {
      startTimeOverlay = startTime.overlayPostcode;
    }
    if (type === "sector") {
      startTimeOverlay = startTime.overlaySector;
    }
    if (type === "outer") {
      startTimeOverlay = startTime.overlayOuter;
    }
  }
  return startTimeOverlay;
};

const getStartTimeFromAllocation = ({
  key,
  postcodeAllocation,
  routesObject,
  selectedTimeBand,
}) => {
  const postcode = new Postcode(key);
  const allocation = {};
  if (postcodeAllocation[postcode.toPostcode()]) {
    allocation[POSTCODE] = postcodeAllocation[postcode.toPostcode()];
  }
  if (postcodeAllocation[postcode.toSector()]) {
    allocation[SECTOR] = postcodeAllocation[postcode.toSector()];
  }
  if (postcodeAllocation[postcode.toOuter()]) {
    allocation[OUTER] = postcodeAllocation[postcode.toOuter()];
  }

  const postcodeRoutes = getDataFromRouteNumber({
    allocation: allocation.POSTCODE,
    routesObject,
    routePostcodeKey: "postcode",
    postcodeKey: "postcode",
    postcode: postcode.toPostcode(),
  });
  const sectorRoutes = getDataFromRouteNumber({
    allocation: allocation.SECTOR,
    routesObject,
    routePostcodeKey: "sector",
    postcodeKey: "postcodeSector",
    postcode: postcode.toSector(),
  });
  const outerRoutes = getDataFromRouteNumber({
    allocation: allocation.OUTER,
    routesObject,
    routePostcodeKey: "outer",
    postcodeKey: "postcodeOuter",
    postcode: postcode.toOuter(),
  });

  let startTime = "-23:00";

  postcodeRoutes &&
    postcodeRoutes.forEach((route) => {
      if (route.readyTime < selectedTimeBand && route.readyTime > startTime) {
        startTime = route.readyTime;
      }
    });

  sectorRoutes &&
    sectorRoutes.forEach((route) => {
      if (route.readyTime < selectedTimeBand && route.readyTime > startTime) {
        startTime = route.readyTime;
      }
    });

  outerRoutes &&
    outerRoutes.forEach((route) => {
      if (route.readyTime < selectedTimeBand && route.readyTime > startTime) {
        startTime = route.readyTime;
      }
    });

  return startTime;
};

const getDataFromRouteNumber = ({
  allocation = [],
  routesObject,
  routePostcodeKey = "outer",
  postcodeKey = "postcodeOuter",
  postcode,
}) => {
  return allocation.map((routeNo) => {
    const route = routesObject[routeNo];
    let allocation = null;
    route[routePostcodeKey].forEach((p) => {
      if (p[postcodeKey] === postcode) {
        allocation = p;
        return;
      }
    });
    if (!allocation) return null;
    return {
      ...allocation,
      routeCode: route.routeCode,
      readyTime: route.plannedReadyTimeAsString,
    };
  });
};

export const applyChangeToProfile = async ({
  routes,
  routeConfig,
  redirectConfig,
  routesImpacted,
  preEditRoutes,
  preEditRoutesTimes,
  routesByOuter,
  routesTimes,
  cutOffTimes,
  volumeData,
  overrideAreas,
  pickupAreas,
  locationConfig,
  lookupTables,
  change,
}) => {
  routes = applyChangeToRoutes({
    change,
    routes,
  });
  routeConfig = applyChangeToRouteConfig({ change, routeConfig });
  redirectConfig = applyChangeToRedirectConfig({
    change,
    routeConfig,
    redirectConfig,
  });

  let overlayDetails;
  ({ routes, overlayDetails, routesByOuter, routesTimes, cutOffTimes } =
    await applyChangeToTimesAndVolumes({
      routeConfig,
      redirectConfig,
      routes,
      routesByOuter,
      routesTimes,
      cutOffTimes,
      volumeData,
      overrideAreas,
      pickupAreas,
      locationConfig,
      lookupTables,
      change,
    }));

  ({ routes, routesImpacted } = restoreRoutesWithRevertedChanges({
    routes,
    preEditRoutes,
    routesTimes,
    preEditRoutesTimes,
    routesImpacted,
    change,
  }));

  return {
    routes,
    routeConfig,
    redirectConfig,
    routesImpacted,
    overlayDetails,
    routesByOuter,
    routesTimes,
    cutOffTimes,
  };
};

const restoreRoutesWithRevertedChanges = ({
  routes,
  preEditRoutes,
  routesTimes,
  preEditRoutesTimes,
  routesImpacted,
  change,
}) => {
  const DEFAULT_TIMES = {
    outer: {},
    sector: {},
    postcode: {},
  };
  routesImpacted = { ...routesImpacted };
  change.impactRouteCodes.forEach((routeCode) => {
    const hasEqualCollections = isEqual(
      preEditRoutesTimes[routeCode] || DEFAULT_TIMES,
      routesTimes[routeCode] || DEFAULT_TIMES
    );

    if (!hasEqualCollections) routesImpacted[routeCode] = { optimised: false };
    else delete routesImpacted[routeCode];
  });

  const routesByRouteCode = preEditRoutes.reduce((acc, route) => {
    acc[route.routeCode] = route;
    return acc;
  }, {});

  return {
    routes: routes.map((route) =>
      routesImpacted[route.routeCode]
        ? route
        : routesByRouteCode[route.routeCode]
    ),
    routesImpacted,
  };
};

export const addMissingOuters = ({
  routeConfig = { routeOuter: {}, routeSector: {}, routePostcode: {} },
  lookupTables,
  allLevels = false,
}) => {
  let newConfig = { ...routeConfig };
  const outers = difference(
    keys(lookupTables.OUTER),
    keys(newConfig.routeOuter)
  );
  each(outers, (outer) => {
    newConfig.routeOuter[outer] = { outer };
  });

  if (allLevels) {
    const sectors = difference(
      keys(lookupTables.SECTOR),
      keys(newConfig.routeSector)
    );
    each(sectors, (sector) => {
      newConfig.routeSector[sector] = { sector };
    });
    const postcodes = difference(
      keys(lookupTables.POSTCODE),
      keys(newConfig.routePostcode)
    );
    each(postcodes, (postcode) => {
      newConfig.routePostcode[postcode] = { postcode };
    });
  }

  return { ...routeConfig, ...newConfig };
};
